// `DowngradeConfirm` — modal confirming a downgrade (US-194).
//
// When current accounts > target plan's account quota, the modal renders
// a blocked banner asking the user to disconnect first (per `billing.md`
// + open-questions.md Q10). Otherwise, lists feature loss and confirms.

import { useT } from '@barel/i18n';
import {
  Banner,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@barel/ui';

import type { BillingPlan } from '@barel/api-client/react';

const PLAN_LABELS: Record<
  BillingPlan,
  { name: string; accounts: number; campaigns: number; ai: number }
> = {
  discover: { name: 'Discover', accounts: 1, campaigns: 1, ai: 500 },
  pro: { name: 'Pro', accounts: 1, campaigns: 5, ai: 1500 },
  scale: { name: 'Scale', accounts: 2, campaigns: 10, ai: 4000 },
  agency: { name: 'Agency', accounts: 3, campaigns: Infinity, ai: Infinity },
};

export interface DowngradeConfirmProps {
  open: boolean;
  targetPlan: BillingPlan | null;
  currentAccounts: number;
  onConfirm: () => void;
  onClose: () => void;
  isPending?: boolean;
}

export function DowngradeConfirm({
  open,
  targetPlan,
  currentAccounts,
  onConfirm,
  onClose,
  isPending,
}: DowngradeConfirmProps) {
  const t = useT('billing');
  if (!targetPlan) return null;
  const meta = PLAN_LABELS[targetPlan];
  const blocked = currentAccounts > meta.accounts;
  const accountsLost = Math.max(0, currentAccounts - meta.accounts);

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent size="sm" data-testid="downgrade-confirm">
        <DialogHeader title={t('downgrade.title', { name: meta.name })} />
        <DialogBody>
          <p className="text-sm text-secondary">{t('downgrade.body', { name: meta.name })}</p>

          {blocked ? (
            <Banner tone="warning" data-testid="downgrade-blocked">
              {t('downgrade.blocked', {
                currentAccounts,
                targetLimit: meta.accounts,
                targetPlan: meta.name,
              })}
            </Banner>
          ) : (
            <ul className="mt-3 flex list-disc flex-col gap-1 pl-4 text-xs text-secondary">
              {accountsLost > 0 ? (
                <li>{t('downgrade.loseAccounts', { count: accountsLost })}</li>
              ) : null}
              {meta.campaigns !== Infinity ? (
                <li>{t('downgrade.loseCampaigns', { count: meta.campaigns })}</li>
              ) : null}
              {meta.ai !== Infinity ? <li>{t('downgrade.loseAi', { count: meta.ai })}</li> : null}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} size="sm">
            {t('upgrade.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={blocked}
            loading={isPending}
            size="sm"
          >
            {t('downgrade.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
